import { useState, useMemo } from 'react';
import { Divider, IconButton, Grid, Typography, Box } from '@mui/material';
import { colors, UserRole, DataTable } from 'remote/global';
import { useAuthClientJwtQuery } from 'api/queries/auth';
import { useOperatorsUsersQuery } from 'api/queries/operators';
import { BorderColorOutlined } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { AccountMemberSchemaType } from 'components/forms/AccountFactory/types';
import AccountInvitationForm from 'components/forms/AccountFactory/AccountInvitationForm';
import AccountEditMemberModal from 'components/modals/AccountEditMemberModal';

function SettingsUsersView() {
  const [isEditMemberModalOpen, setIsEditMemberModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AccountMemberSchemaType | null>(null);
  const [pageNo, setPageNo] = useState(0);
  const { data: userData } = useAuthClientJwtQuery();
  const { data: users, isLoading } = useOperatorsUsersQuery({ pageNo: String(pageNo) });
  const userId = userData?.['husky.userId'];
  const userRole = userData?.['husky.roles'];
  const { t } = useTranslation();

  const columns: GridColDef[] = useMemo(() => {
    const isDisabled = (id: number) => {
      if (id === userId && userRole === UserRole.OPERATOR_ADMIN) {
        return true;
      }
      return false;
    };

    return [
      {
        field: 'name',
        headerName: t('UsersView.table.header.name').toString(),
        flex: 2,
        renderCell: ({ row }) => {
          return (
            <Box>
              <Typography
                sx={{
                  color: colors.gray2,
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >{`${row.contactInfo.firstName} ${row.contactInfo.lastName}`}</Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  color: colors.gray1,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                {row.contactInfo.email}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'Role',
        headerName: t('UsersView.table.header.role').toString(),
        flex: 1,
        valueGetter: (params) => {
          return t(`UserRole.${params.row.role}`);
        },
      },
      {
        field: ' ',
        align: 'right',
        headerName: '',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => (
          <IconButton
            size='large'
            sx={{ padding: 0, color: colors.uiBlue600, marginRight: '48px' }}
            disabled={isDisabled(row.id)}
            onClick={() => {
              setIsEditMemberModalOpen(true);
              setSelectedUser(row);
            }}
          >
            <BorderColorOutlined />
          </IconButton>
        ),
      },
    ];
  }, [t, userId, userRole]);

  const handleClearEditMemberModalState = () => {
    setIsEditMemberModalOpen(false);
    setSelectedUser(null);
  };

  return (
    <>
      <AccountEditMemberModal
        open={isEditMemberModalOpen}
        setOpen={() => setIsEditMemberModalOpen(false)}
        onClose={handleClearEditMemberModalState}
        defaultValues={selectedUser!}
      />
      <Grid item xs={12} lg={6}>
        <Typography
          color={colors.uiBlue600}
          mb='12px'
          fontWeight='600'
          fontSize='18px'
          style={{ textAlign: 'left' }}
        >
          {t('UsersView.title')}
        </Typography>
        <Typography
          fontSize='16px'
          variant='body2'
          mb='29px'
          color={colors.gray1}
          style={{ textAlign: 'left' }}
        >
          {t('UsersView.subtitle')}
        </Typography>
      </Grid>
      <Grid item xs={12} lg={6}>
        <AccountInvitationForm />
      </Grid>
      <Grid item xs={12}>
        <Divider style={{ width: '100%', marginTop: '24px', marginBottom: '24px' }} />
      </Grid>
      <Grid item xs={12}>
        <DataTable
          rows={users ?? []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          onPageChange={setPageNo}
          t={t}
        />
      </Grid>
    </>
  );
}

export default SettingsUsersView;
